/**
 * The template pairs by kind — the one place that says which show style goes
 * with which studio: the simulators' (`studioTemplate.ts`) on a host and port
 * block, and one vMix's (`vmixTemplate.ts`) on a host and TCP API port. The
 * CLI's `studios create --template`, the SPA's "New studio" dialog and its
 * onboarding wizard build from here, so the three cannot drift apart.
 */
import type { JsonValue } from '@norna/domain/primitives.js';
import { showStyleTemplate, studioTemplate } from './studioTemplate.js';
import { vmixShowStyleTemplate, vmixStudioTemplate, VMIX_TCP_PORT } from './vmixTemplate.js';

type JsonObject = { [key: string]: JsonValue };

export const TEMPLATE_KINDS = ['sim', 'vmix'] as const;
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];

/** Where a template's devices are: the simulators' port block (base + 5, 6, 7), or vMix's TCP API. */
export type TemplateTarget = { readonly kind: 'sim'; readonly host: string; readonly basePort: number } | { readonly kind: 'vmix'; readonly host: string; readonly port: number };

/** The port a vMix template names unless told: vMix's TCP API default. */
export const DEFAULT_VMIX_PORT = VMIX_TCP_PORT;

/** What a template kind is called where a person picks one. */
export const TEMPLATE_LABELS: Readonly<Record<TemplateKind, string>> = { sim: 'Simulated devices', vmix: 'vMix' };

/** The show style a kind's studio is mapped for. */
export function showStyleTemplateOf(kind: TemplateKind): JsonObject {
  return kind === 'vmix' ? vmixShowStyleTemplate() : showStyleTemplate();
}

/** The studio config of a template, on its target. */
export function studioTemplateOf(target: TemplateTarget): JsonObject {
  return target.kind === 'vmix' ? vmixStudioTemplate(target.host, target.port) : studioTemplate(target.host, target.basePort);
}

/** A template's show style and studio together. */
export function templatePairOf(target: TemplateTarget): { readonly showStyle: JsonObject; readonly studio: JsonObject } {
  return { showStyle: showStyleTemplateOf(target.kind), studio: studioTemplateOf(target) };
}

/** Which template a show style config was made from, by its layers: vMix's (`vmix_*`) or the simulators' template's; anything else is not claimed. */
export function templateKindOfShowStyle(config: Readonly<Record<string, JsonValue>>): TemplateKind | null {
  const layersOf = (c: Readonly<Record<string, JsonValue>>): string[] => {
    const layers = c['layers'];
    return layers === null || typeof layers !== 'object' || Array.isArray(layers) ? [] : Object.keys(layers);
  };
  const layers = layersOf(config);
  if (layers.length === 0) return null;
  if (layers.every((l) => l.startsWith('vmix_'))) return 'vmix';
  const sim = new Set(layersOf(showStyleTemplate()));
  return layers.every((l) => sim.has(l)) ? 'sim' : null;
}

/** Which template a stored studio config was made from, by its devices: only `vmix` devices is vMix's, only simulated ones the simulators'; anything else is not claimed. */
export function templateKindOfStudio(config: Readonly<Record<string, JsonValue>>): TemplateKind | null {
  const devices = config['devices'];
  if (devices === null || typeof devices !== 'object' || Array.isArray(devices)) return null;
  const types = Object.values(devices).map((d) => (d !== null && typeof d === 'object' && !Array.isArray(d) ? d['type'] : undefined));
  if (types.length > 0 && types.every((t) => t === 'vmix')) return 'vmix';
  if (types.length > 0 && types.every((t) => typeof t === 'string' && t.startsWith('sim-'))) return 'sim';
  return null;
}
